import { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';

function fmt$(n) {
  const v = parseFloat(n ?? 0);
  return v === Math.round(v)
    ? '$' + Math.round(v).toLocaleString()
    : '$' + v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatExactTime(decimalHours) {
  const totalMinutes = Math.round(parseFloat(decimalHours || 0) * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (h === 0) return `${m} min`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

function fmtPhone(raw) {
  if (!raw) return raw;
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 10) return `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
  if (digits.length === 11 && digits[0] === '1') return `+1 (${digits.slice(1,4)}) ${digits.slice(4,7)}-${digits.slice(7)}`;
  return raw;
}

function formatAddressLines(obj) {
  if (!obj) return [];
  const lines = [];
  if (obj.address_line1) lines.push(obj.address_line1);
  if (obj.address_line2) lines.push(obj.address_line2);
  const cityState = [obj.city, obj.state].filter(Boolean).join(', ');
  const cityStateLine = obj.zip ? `${cityState} ${obj.zip}`.trim() : cityState;
  if (cityStateLine) lines.push(cityStateLine);
  if (obj.country) lines.push(obj.country);
  return lines;
}

const STATUS_COLORS = { draft: '#f6c26b', sent: '#63d3f7', paid: '#68d391' };

export default function PublicInvoice({ token }) {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');

  useEffect(() => {
    if (!token) { setError('Invalid invoice link.'); setLoading(false); return; }
    supabase.functions.invoke('get-public-invoice', { body: { token } })
      .then(({ data: res, error: fnErr }) => {
        if (fnErr || !res?.ok) { setError('Invoice not found or link has expired.'); }
        else { setData(res); }
        setLoading(false);
      });
  }, [token]);

  const handlePrint = () => {
    if (!data) return;
    const { invoice, org, client } = data;
    const lines = (invoice.invoice_line_items ?? []).sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    const periodLabel = invoice.period
      ? new Date(invoice.period + '-02').toLocaleString('en-US', { month: 'long', year: 'numeric' })
      : '';
    const effectiveLogo = org?.invoice_logo_url || org?.logo_url || null;
    const orgAddrLines = org?.invoice_show_address !== false ? formatAddressLines(org) : [];
    const clientAddrLines = org?.invoice_show_client_address !== false ? formatAddressLines(client) : [];
    const clientPhone = org?.invoice_show_client_phone !== false ? (client?.phone ? fmtPhone(client.phone) : null) : null;

    const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>Invoice ${invoice.invoice_number}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, Helvetica, sans-serif; color: #1e293b; padding: 48px; max-width: 800px; margin: 0 auto; }
    @media print { body { padding: 32px; } }
  </style>
</head>
<body>
  <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:40px;">
    <div>
      ${effectiveLogo ? `<img src="${effectiveLogo}" alt="${org?.name || ''}" style="max-height:60px;max-width:180px;margin-bottom:10px;display:block;" />` : ''}
      <div style="font-size:22px;font-weight:bold;">${org?.name || ''}</div>
      ${orgAddrLines.map(l => `<div style="font-size:12px;color:#64748b;">${l}</div>`).join('')}
      ${org?.invoice_show_phone !== false && org?.phone ? `<div style="font-size:12px;color:#64748b;">${fmtPhone(org.phone)}</div>` : ''}
      ${org?.invoice_show_website !== false && org?.website ? `<div style="font-size:12px;color:#64748b;">${org.website}</div>` : ''}
    </div>
    <div style="text-align:right;">
      <div style="font-size:36px;font-weight:bold;letter-spacing:-1px;">INVOICE</div>
      <div style="font-size:15px;color:#64748b;margin-top:4px;">${invoice.invoice_number}</div>
      ${periodLabel ? `<div style="font-size:13px;color:#94a3b8;margin-top:2px;">${periodLabel}</div>` : ''}
    </div>
  </div>
  <div style="display:flex;justify-content:space-between;margin-bottom:32px;padding-bottom:20px;border-bottom:2px solid #e2e8f0;">
    <div>
      <div style="font-size:11px;font-weight:bold;color:#94a3b8;text-transform:uppercase;letter-spacing:0.8px;margin-bottom:6px;">Bill To</div>
      <div style="font-size:16px;font-weight:600;">${client?.billing_name || client?.name || ''}</div>
      ${client?.billing_contact_name ? `<div style="font-size:13px;color:#475569;">Attn: ${client.billing_contact_name}</div>` : ''}
      ${clientAddrLines.map(l => `<div style="font-size:12px;color:#64748b;">${l}</div>`).join('')}
      ${clientPhone ? `<div style="font-size:12px;color:#64748b;">${clientPhone}</div>` : ''}
    </div>
    <div style="text-align:right;font-size:13px;line-height:2;">
      ${invoice.issued_date ? `<div><span style="color:#94a3b8;">Invoice Date&nbsp;&nbsp;</span><strong>${invoice.issued_date}</strong></div>` : ''}
      ${invoice.due_date ? `<div><span style="color:#94a3b8;">Due Date&nbsp;&nbsp;</span><strong>${invoice.due_date}</strong></div>` : ''}
      ${invoice.payment_terms ? `<div><span style="color:#94a3b8;">Terms&nbsp;&nbsp;</span><strong>${invoice.payment_terms}</strong></div>` : ''}
      ${invoice.po_number ? `<div><span style="color:#94a3b8;">PO Number&nbsp;&nbsp;</span><strong>${invoice.po_number}</strong></div>` : ''}
    </div>
  </div>
  <table style="width:100%;border-collapse:collapse;margin-bottom:24px;">
    <thead>
      <tr style="border-bottom:2px solid #e2e8f0;">
        <th style="text-align:left;padding:8px 0;font-size:11px;font-weight:bold;color:#94a3b8;text-transform:uppercase;letter-spacing:0.5px;">Description</th>
        <th style="text-align:right;padding:8px 0;font-size:11px;font-weight:bold;color:#94a3b8;text-transform:uppercase;letter-spacing:0.5px;width:70px;">Qty</th>
        <th style="text-align:right;padding:8px 0;font-size:11px;font-weight:bold;color:#94a3b8;text-transform:uppercase;letter-spacing:0.5px;width:80px;">Rate</th>
        <th style="text-align:right;padding:8px 0;font-size:11px;font-weight:bold;color:#94a3b8;text-transform:uppercase;letter-spacing:0.5px;width:90px;">Amount</th>
      </tr>
    </thead>
    <tbody>
      ${lines.map(l => `
        <tr>
          <td style="padding:11px 0;font-size:13px;color:#1e293b;border-bottom:1px solid #f1f5f9;">${l.description || ''}</td>
          <td style="padding:11px 0;text-align:right;font-size:13px;color:#64748b;border-bottom:1px solid #f1f5f9;">${l.hours != null ? parseFloat(l.hours).toLocaleString('en-US', { maximumFractionDigits: 2 }) : '—'}</td>
          <td style="padding:11px 0;text-align:right;font-size:13px;color:#64748b;border-bottom:1px solid #f1f5f9;">${l.rate != null ? fmt$(l.rate) : '—'}</td>
          <td style="padding:11px 0;text-align:right;font-size:13px;font-weight:600;color:#1e293b;border-bottom:1px solid #f1f5f9;">${fmt$(l.amount)}</td>
        </tr>`).join('')}
    </tbody>
  </table>
  <div style="display:flex;justify-content:flex-end;margin-bottom:32px;">
    <div style="min-width:220px;border-top:2px solid #1e293b;padding-top:12px;display:flex;justify-content:space-between;align-items:baseline;">
      <span style="font-size:15px;font-weight:bold;">Total Due</span>
      <span style="font-size:22px;font-weight:bold;">${fmt$(invoice.total)}</span>
    </div>
  </div>
  ${invoice.notes ? `
  <div style="padding:16px;background:#f8fafc;border-radius:6px;border:1px solid #e2e8f0;">
    <div style="font-size:11px;font-weight:bold;color:#94a3b8;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:6px;">Memo</div>
    <div style="font-size:13px;color:#475569;line-height:1.6;">${invoice.notes}</div>
  </div>` : ''}
</body>
</html>`;

    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(html);
    w.document.close();
    w.focus();
    // Wait for images (logo) to fully load before triggering print
    w.onload = () => w.print();
    // Fallback: if onload already fired (readyState complete), print immediately
    if (w.document.readyState === 'complete') w.print();
  };

  if (loading) return (
    <div style={{ minHeight: '100vh', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Arial, sans-serif', color: '#64748b', fontSize: '14px' }}>
      Loading invoice…
    </div>
  );

  if (error) return (
    <div style={{ minHeight: '100vh', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Arial, sans-serif' }}>
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔒</div>
        <div style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b', marginBottom: '8px' }}>Invoice Not Found</div>
        <div style={{ fontSize: '14px', color: '#64748b' }}>{error}</div>
      </div>
    </div>
  );

  const { invoice, org, client } = data;
  const lines = (invoice.invoice_line_items ?? []).sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  const periodLabel = invoice.period
    ? new Date(invoice.period + '-02').toLocaleString('en-US', { month: 'long', year: 'numeric' })
    : '';
  const effectiveLogo = org?.invoice_logo_url || org?.logo_url || null;
  const orgAddrLines = org?.invoice_show_address !== false ? formatAddressLines(org) : [];
  const clientAddrLines = org?.invoice_show_client_address !== false ? formatAddressLines(client) : [];
  const clientPhone = org?.invoice_show_client_phone !== false ? (client?.phone || null) : null;
  const statusColor = STATUS_COLORS[invoice.status] || '#94a3b8';
  const statusLabel = invoice.status ? invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1) : '';
  // Nothing left to pay once the invoice is paid, while a bank payment is still
  // clearing, or the moment Stripe sends the client back here (?paid=1): the
  // webhook that flips the status can land a second after this page loads, and
  // a Pay Now button right after paying invites a second payment.
  const isPaid = invoice.status === 'paid';
  const justPaid = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('paid') === '1';
  const paymentClearing = !isPaid && !!invoice.stripe_payment_pending_at;
  const showPay = !isPaid && !paymentClearing && !justPaid;
  // Stripe (when enabled) takes priority over a manually-pasted payment link —
  // it mints a fresh Checkout Session on click so the URL never goes stale.
  const payHref = !showPay ? null : (org?.stripe_enabled && invoice.public_token)
    ? `${supabase.supabaseUrl}/functions/v1/stripe-pay-redirect?token=${invoice.public_token}`
    : (invoice?.payment_link || org?.invoice_payment_link || null);
  const paymentNote = isPaid ? 'Paid. Thank you!'
    : (justPaid || paymentClearing) ? 'Payment received. Thank you! It may take a few moments to show as paid here, or a few business days for a bank transfer to clear.'
    : null;
  const paymentNoteColor = isPaid ? STATUS_COLORS.paid : STATUS_COLORS.sent;
  // The client's own chosen brand colors, not TimeKeep's -- this document is
  // the org's invoice, sent under their own name, not a TimeKeep-branded page.
  const brandBg = org?.primary_color || '#111D39';
  const brandAccent = org?.accent_color || '#00A0C8';

  return (
    <div style={{ minHeight: '100vh', background: '#f1f5f9', fontFamily: 'Arial, Helvetica, sans-serif', padding: '32px 16px' }}>

      {/* Toolbar */}
      <div style={{ maxWidth: '760px', margin: '0 auto 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
        <div style={{ fontSize: '13px', color: '#64748b' }}>
          <strong style={{ color: '#1e293b' }}>{org?.name}</strong> · Invoice {invoice.invoice_number}
          <span style={{ marginLeft: '8px', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '700', background: `${statusColor}22`, color: statusColor }}>
            {statusLabel}
          </span>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={handlePrint}
            style={{ padding: '8px 18px', borderRadius: '6px', border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: '600', color: '#475569' }}>
            🖨 Save as PDF
          </button>
          {payHref && (
            <a href={payHref} target="_blank" rel="noreferrer"
              style={{ padding: '8px 20px', borderRadius: '6px', border: 'none', background: brandBg, color: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: '700', textDecoration: 'none', display: 'inline-block' }}>
              Pay Now
            </a>
          )}
        </div>
      </div>

      {/* Invoice card */}
      <div style={{ maxWidth: '760px', margin: '0 auto', background: '#fff', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 2px 16px rgba(0,0,0,0.08)' }}>

        {/* Header */}
        <div style={{ background: brandBg, padding: '24px 40px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ color: brandAccent, fontSize: '20px', fontWeight: '800' }}>{org?.name || ''}</div>
            <div style={{ color: '#94a3b8', fontSize: '12px', marginTop: '2px' }}>Invoice</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ color: '#fff', fontSize: '24px', fontWeight: '700' }}>{invoice.invoice_number}</div>
            {periodLabel && <div style={{ color: '#94a3b8', fontSize: '12px', marginTop: '2px' }}>{periodLabel}</div>}
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: '40px' }}>

          {/* From / To */}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '36px', paddingBottom: '28px', borderBottom: '2px solid #e2e8f0', gap: '20px' }}>
            <div style={{ flex: 1 }}>
              {effectiveLogo && <img src={effectiveLogo} alt={org?.name || ''} style={{ maxHeight: '56px', maxWidth: '180px', objectFit: 'contain', marginBottom: '12px', display: 'block' }} />}
              <div style={{ fontSize: '16px', fontWeight: '700', color: '#1e293b' }}>{org?.name || ''}</div>
              {orgAddrLines.map((l, i) => <div key={i} style={{ fontSize: '12px', color: '#64748b' }}>{l}</div>)}
              {org?.invoice_show_phone !== false && org?.phone && <div style={{ fontSize: '12px', color: '#64748b' }}>{fmtPhone(org.phone)}</div>}
              {org?.invoice_show_website !== false && org?.website && <div style={{ fontSize: '12px', color: '#64748b' }}>{org.website}</div>}
            </div>
            <div style={{ flex: 1, textAlign: 'right' }}>
              <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '8px' }}>Bill To</div>
              <div style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b' }}>{client?.billing_name || client?.name || ''}</div>
              {client?.billing_contact_name && <div style={{ fontSize: '13px', color: '#475569' }}>Attn: {client.billing_contact_name}</div>}
              {clientAddrLines.map((l, i) => <div key={i} style={{ fontSize: '12px', color: '#475569' }}>{l}</div>)}
              {clientPhone && <div style={{ fontSize: '12px', color: '#475569' }}>{clientPhone}</div>}
            </div>
          </div>

          {/* Meta */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '28px' }}>
            <table style={{ fontSize: '13px', borderCollapse: 'collapse' }}>
              <tbody>
                {invoice.issued_date && <tr><td style={{ padding: '4px 20px 4px 0', color: '#94a3b8' }}>Invoice Date</td><td style={{ padding: '4px 0', fontWeight: '600', color: '#1e293b' }}>{invoice.issued_date}</td></tr>}
                {invoice.due_date && <tr><td style={{ padding: '4px 20px 4px 0', color: '#94a3b8' }}>Due Date</td><td style={{ padding: '4px 0', fontWeight: '600', color: '#1e293b' }}>{invoice.due_date}</td></tr>}
                {invoice.payment_terms && <tr><td style={{ padding: '4px 20px 4px 0', color: '#94a3b8' }}>Terms</td><td style={{ padding: '4px 0', fontWeight: '600', color: '#1e293b' }}>{invoice.payment_terms}</td></tr>}
                {invoice.po_number && <tr><td style={{ padding: '4px 20px 4px 0', color: '#94a3b8' }}>PO Number</td><td style={{ padding: '4px 0', fontWeight: '600', color: '#1e293b' }}>{invoice.po_number}</td></tr>}
              </tbody>
            </table>
          </div>

          {/* Line items */}
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '24px' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                <th style={{ textAlign: 'left', padding: '8px 0', fontSize: '11px', fontWeight: 'bold', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Description</th>
                <th style={{ textAlign: 'right', padding: '8px 0', fontSize: '11px', fontWeight: 'bold', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', width: '72px' }}>Qty</th>
                <th style={{ textAlign: 'right', padding: '8px 0', fontSize: '11px', fontWeight: 'bold', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', width: '80px' }}>Rate</th>
                <th style={{ textAlign: 'right', padding: '8px 0', fontSize: '11px', fontWeight: 'bold', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', width: '92px' }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={i}>
                  <td style={{ padding: '12px 0', fontSize: '13px', color: '#1e293b', borderBottom: '1px solid #f1f5f9' }}>{l.description || ''}</td>
                  <td style={{ padding: '12px 0', textAlign: 'right', fontSize: '13px', color: '#64748b', borderBottom: '1px solid #f1f5f9' }}>{l.hours != null ? parseFloat(l.hours).toLocaleString('en-US', { maximumFractionDigits: 2 }) : '—'}</td>
                  <td style={{ padding: '12px 0', textAlign: 'right', fontSize: '13px', color: '#64748b', borderBottom: '1px solid #f1f5f9' }}>{l.rate != null ? fmt$(l.rate) : '—'}</td>
                  <td style={{ padding: '12px 0', textAlign: 'right', fontSize: '13px', fontWeight: '600', color: '#1e293b', borderBottom: '1px solid #f1f5f9' }}>{fmt$(l.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Total */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '32px' }}>
            <div style={{ minWidth: '240px', borderTop: '2px solid #1e293b', paddingTop: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <span style={{ fontSize: '15px', fontWeight: 'bold', color: '#1e293b' }}>Total Due</span>
              <span style={{ fontSize: '26px', fontWeight: 'bold', color: '#1e293b' }}>{fmt$(invoice.total)}</span>
            </div>
          </div>

          {/* Paid / payment received note, in place of Pay Now */}
          {paymentNote && (
            <div style={{ textAlign: 'center', marginBottom: '32px', padding: '14px 20px', borderRadius: '8px', background: `${paymentNoteColor}22`, color: paymentNoteColor, fontSize: '14px', fontWeight: '700' }}>
              {paymentNote}
            </div>
          )}

          {/* Pay Now button */}
          {payHref && (
            <div style={{ textAlign: 'center', marginBottom: '32px' }}>
              <a href={payHref} target="_blank" rel="noreferrer"
                style={{ display: 'inline-block', background: brandBg, color: '#fff', fontSize: '16px', fontWeight: '700', padding: '16px 48px', borderRadius: '8px', textDecoration: 'none', letterSpacing: '0.3px' }}>
                Pay Now
              </a>
              <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '8px' }}>Secure payment link</div>
            </div>
          )}

          {/* Memo */}
          {invoice.notes && (
            <div style={{ padding: '16px 20px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '24px' }}>
              <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>Memo</div>
              <div style={{ fontSize: '13px', color: '#475569', lineHeight: '1.6' }}>{invoice.notes}</div>
            </div>
          )}

          {/* Attachments */}
          {data?.attachments?.length > 0 && (
            <div style={{ padding: '16px 20px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>📎 Attachments ({data.attachments.length})</div>
                {data.attachments.length > 1 && (
                  <button
                    onClick={() => {
                      data.attachments.filter(a => a.url).forEach((att, i) => {
                        setTimeout(() => {
                          const a = document.createElement('a');
                          a.href = att.url;
                          a.download = att.file_name;
                          a.target = '_blank';
                          document.body.appendChild(a);
                          a.click();
                          document.body.removeChild(a);
                        }, i * 400);
                      });
                    }}
                    style={{ background: brandBg, color: '#fff', border: 'none', borderRadius: '5px', fontSize: '12px', fontWeight: '600', padding: '5px 14px', cursor: 'pointer' }}>
                    Download All
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {data.attachments.map(att => (
                  <div key={att.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '6px' }}>
                    <span style={{ fontSize: '16px' }}>{att.mime_type?.startsWith('image/') ? '🖼' : '📄'}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '13px', color: '#1e293b', fontWeight: '500', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{att.file_name}</div>
                      {att.file_size && <div style={{ fontSize: '11px', color: '#94a3b8' }}>{(att.file_size / 1024).toFixed(0)} KB</div>}
                    </div>
                    {att.url && (
                      <a href={att.url} target="_blank" rel="noreferrer" download={att.file_name}
                        style={{ display: 'inline-block', padding: '5px 14px', background: brandBg, color: '#fff', borderRadius: '5px', fontSize: '12px', fontWeight: '600', textDecoration: 'none', flexShrink: 0 }}>
                        Download
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Footer */}
          <div style={{ textAlign: 'center', fontSize: '13px', color: '#94a3b8', borderTop: '1px solid #e2e8f0', paddingTop: '20px' }}>
            Thank you for your business.
            {org?.website && <div style={{ marginTop: '4px' }}><a href={org.website} style={{ color: brandAccent, textDecoration: 'none' }}>{org.website}</a></div>}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: '760px', margin: '16px auto', textAlign: 'center', fontSize: '11px', color: '#94a3b8' }}>
        Powered by TimeKeep
      </div>
    </div>
  );
}
